import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Layout from "./components/layout/Layout";
import ProtectedRoute from "./components/layout/ProtectedRoute";
import { AuthProvider } from "./lib/auth";
import { ThemeProvider } from "./lib/theme";
import { JobProvider } from "./context/JobContext";
import { ErrorProvider } from "./context/ErrorContext";
import GlobalErrorModal from "./components/ui/GlobalErrorModal";
import GlobalSpinner from "./components/ui/GlobalSpinner";
import PageLoading from "./components/ui/PageLoading";

const AccountPage = lazy(() => import("./pages/AccountPage"));
const CertificatesPage = lazy(() => import("./pages/CertificatesPage"));
const ConfigPage = lazy(() => import("./pages/ConfigPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DNSPage = lazy(() => import("./pages/DNSPage"));
const DocsPage = lazy(() => import("./pages/DocsPage"));
const LockedDomainsPage = lazy(() => import("./pages/LockedDomainsPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const MigrationPage = lazy(() => import("./pages/MigrationPage"));
const ProcurementPage = lazy(() => import("./pages/ProcurementPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const RayIdPage = lazy(() => import("./pages/RayIdPage"));
const SecurityPage = lazy(() => import("./pages/SecurityPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const WebDetectivePage = lazy(() => import("./pages/WebDetectivePage"));
const WorkersPage = lazy(() => import("./pages/WorkersPage"));

function App() {
  return (
    <AuthProvider>
      <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
        <ErrorProvider>
          <JobProvider>
            <Suspense fallback={<PageLoading />}>
              <Routes>
                {/* Public Routes - No SSO Required */}
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="/login" element={<LoginPage />} />

                {/* Protected Routes - SSO Required */}
                <Route
                  path="/v1/*"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Suspense fallback={<PageLoading />}>
                        <Routes>
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/procurement" element={<ProcurementPage />} />
                          <Route path="/migration" element={<MigrationPage />} />
                          <Route path="/account" element={<AccountPage />} />
                          <Route path="/dns" element={<DNSPage />} />
                          <Route path="/locked-domains" element={<LockedDomainsPage />} />
                          <Route path="/workers" element={<WorkersPage />} />
                          <Route path="/projects" element={<ProjectsPage />} />
                          <Route path="/certificates" element={<CertificatesPage />} />
                          <Route path="/panic" element={<Navigate to="/v1/dashboard" replace />} />
                          <Route path="/config" element={<ConfigPage />} />
                          <Route path="/support" element={<SupportPage />} />
                          <Route path="/web-detective" element={<WebDetectivePage />} />
                          <Route path="/ray-id" element={<RayIdPage />} />
                          <Route path="/security" element={<SecurityPage />} />
                          <Route path="/documentation/:tab?" element={<DocsPage />} />
                          <Route path="/documentation" element={<DocsPage />} />
                          <Route path="*" element={<Navigate to="/v1/web-detective" replace />} />
                        </Routes>
                        </Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Suspense>
            <GlobalErrorModal />
            <GlobalSpinner />
          </JobProvider>
        </ErrorProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
